import React from "react";
import mentor from "@content/mentor.json";
import features from "@content/features.json";

const ICONS = {
    labs: (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7">
            <rect x="3" y="4" width="18" height="12" rx="2" />
            <path d="M8 20h8M12 16v4" />
        </svg>
    ),
    troubleshoot: (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7">
            <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2.3-2.3z" />
        </svg>
    ),
    packets: (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7">
            <circle cx="5" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <path d="M7 12h3M14 12h3" />
        </svg>
    ),
    mentor: (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7">
            <circle cx="12" cy="8" r="3.5" />
            <path d="M4 20a8 8 0 0 1 16 0" />
        </svg>
    ),
    live: (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7">
            <rect x="3" y="6" width="14" height="12" rx="2" />
            <path d="M21 8l-4 3 4 3z" />
        </svg>
    ),
};

const Trainer = () => {
    return (
        <>
            {/* Why us */}
            <section className="section" id="why" data-testid="why-section">
                <div className="container-p">
                    <div className="max-w-3xl">
                        <span className="eyebrow">Why Students Love Grads2Pro</span>
                        <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-tight text-slate-900">
                            Learn Like You're Already{" "}
                            <span className="text-brand">Working in a Company.</span>
                        </h2>
                        <p className="mt-4 text-dim text-base md:text-lg">
                            This isn't another networking course. It's the closest thing to actually doing
                            the job — before you get the job.
                        </p>
                    </div>

                    <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {features.map((f) => (
                            <div key={f.title} className="panel panel-hover p-5" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g,"-")}`}>
                                <div className="w-11 h-11 rounded-lg border border-[rgba(124,231,59,0.3)] bg-[rgba(124,231,59,0.06)] text-brand flex items-center justify-center">
                                    {ICONS[f.icon] || ICONS.live}
                                </div>
                                <h3 className="mt-4 font-display text-lg font-bold text-slate-900">
                                    {f.title}
                                </h3>
                                <p className="mt-2 text-sm text-dim leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trainer */}
            <section className="section" id="trainer" data-testid="trainer-section">
                <div className="container-p">
                    <div className="grid lg:grid-cols-[400px_1fr] gap-6 items-start">
                        {/* Photo card */}
                        <div className="relative">
                            <div
                                className="absolute -inset-3 rounded-2xl pointer-events-none"
                                style={{
                                    background:
                                        "radial-gradient(circle at 30% 20%, rgba(124,231,59,0.25), transparent 60%)",
                                }}
                                aria-hidden
                            />
                            <div className="panel p-0 overflow-hidden relative">
                                <img
                                    src={mentor.image}
                                    alt={`${mentor.name} — Network & Security Instructor at Grads2Pro`}
                                    width="400"
                                    height="400"
                                    className="w-full h-auto block"
                                    loading="lazy"
                                    decoding="async"
                                    data-testid="trainer-image"
                                />
                                <div className="absolute left-4 right-4 bottom-4 rounded-xl bg-white/95 backdrop-blur border border-[var(--border)] shadow-lg px-4 py-3">
                                    <div className="font-display text-lg font-bold text-slate-900 leading-tight">
                                        {mentor.name}
                                    </div>
                                    <div className="mt-0.5 font-mono text-[11px] tracking-widest uppercase text-slate-500">
                                        A.K.A {mentor.nickname} · {mentor.role}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bio */}
                        <div>
                            <div className="font-mono text-xs uppercase tracking-widest text-brand">
                                // A note from your instructor
                            </div>
                            <h2
                                className="mt-3 font-display text-3xl md:text-5xl font-extrabold leading-tight text-slate-900"
                                data-testid="trainer-heading"
                            >
                                Meet Your Mentor
                            </h2>
                            <blockquote className="mt-5 border-l-2 border-brand pl-4 text-lg md:text-xl italic text-slate-900">
                                "{mentor.quote}"
                            </blockquote>
                            <div className="mt-5 space-y-4 text-dim text-base md:text-[17px] leading-relaxed">
                                {mentor.description.split("\n\n").map((p, i) => (
                                    <p key={i}>{p}</p>
                                ))}
                            </div>

                            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {mentor.stats.map((s) => (
                                    <div key={s.label} className="panel p-3">
                                        <div className="font-display font-bold text-slate-900 text-lg leading-tight">
                                            {s.value}
                                        </div>
                                        <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-dim">
                                            {s.label}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Trainer;
